import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useServices } from "@/services/hooks/useServices";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Download, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_private/mailboxes/import")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { mailboxService } = useServices();
  const [isLoading, setIsLoading] = useState(false);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.type === "text/csv") {
      setSelectedFile(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    try {
      setIsLoading(true);
      const response =
        await mailboxService.importMailboxesFromCsv(selectedFile);
      if (response.success > 0) {
        toast.success(`${response.success} mailboxes imported successfully`);
        window.cioanalytics?.track("mailboxes_imported", {
          count: response.success,
        });
      }
      if (response.failed > 0) {
        toast.error(`${response.failed} mailboxes failed to import`);
        window.cioanalytics?.track("mailboxes_import_failed", {
          count: response.failed,
        });
      }

      navigate({ to: ".." });
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      "Email",
      "First Name",
      "Last Name",
      "SMTP Host",
      "SMTP Port",
      "SMTP TLS",
      "SMTP Password",
      "Auth Provider",
      "SMTP Username",
    ];

    const sampleData = [
      "user@example.com",
      "John",
      "Doe",
      "smtp.gmail.com",
      "587",
      "true",
      "your_password",
      "smtp/google/microsoft/aws_api/aws_smtp/sendgrid_smtp/sendgrid_api",
      "your_username",
    ];

    const csvContent = [headers.join(","), sampleData.join(",")].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mailbox_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full h-full">
      <Button
        variant="ghost"
        onClick={() => navigate({ to: ".." })}
        className="mb-6"
      >
        <ChevronLeft className="mr-2 h-4 w-4" />
        Back
      </Button>

      <Card className="mx-auto max-w-2xl">
        <div
          className={`relative flex min-h-[400px] flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${
            isDragging ? "border-primary bg-primary/5" : "border-gray-200"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="rounded-full bg-primary/10 p-4">
              <Upload className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">
                Drop your .CSV file onto this area to upload
              </h3>
              <p className="text-sm text-muted-foreground">
                or click the button below
              </p>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".csv"
              className="sr-only"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="mt-2"
                disabled={isLoading}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose a CSV file
              </Button>
              <Button
                variant="outline"
                className="mt-2"
                onClick={handleDownloadTemplate}
              >
                <Download className="w-4 h-4" />
                Download Sample Template
              </Button>
            </div>
            {selectedFile && (
              <div className="flex items-center gap-2 mt-2">
                <p className="text-sm text-muted-foreground">
                  Selected: {selectedFile.name}
                </p>
                <button
                  onClick={handleRemoveFile}
                  className="text-sm text-red-500 hover:text-red-600"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="mx-auto mt-6 flex max-w-2xl justify-end gap-4">
        <Button variant="outline" onClick={() => navigate({ to: ".." })}>
          Cancel
        </Button>
        <Button
          disabled={!selectedFile || isLoading}
          isLoading={isLoading}
          onClick={handleImport}
        >
          Import
        </Button>
      </div>
    </div>
  );
}
